import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '../../../api';
import type { WorkflowQuery, WorkflowSummary } from '../../../api/areas/registry';
import { Badge } from '../../../components/Badge';
import { DataTable, Field, FilterBar, Pager, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EntityRef } from '../../../components/EntityRef';
import { CorrelationLink, DomainLink, PlainId } from '../../../components/Ids';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { useApi } from '../../../lib/useApi';
import { EnumBadge, humanize, RefreshButton, Since, useEnumLabel, WORKFLOW_TONE } from '../shared';
import { resourceName, WF_STATES } from './vocab';

const ANY = '__any';

/** `/ops/workflows`: durable workflow instances with state and current step (spec §71–§72). */
export function WorkflowsPage() {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  useDocumentTitle(t('workflows.title'));
  const [params, setParams] = useSearchParams();
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    if (k !== 'before') p.delete('before');
    setParams(p);
  };
  const q: WorkflowQuery = {
    state: (params.get('state') || undefined) as WorkflowQuery['state'],
    workflow_type: params.get('workflow_type') || undefined,
    resource_id: params.get('resource_id') || undefined,
    correlation_id: params.get('correlation_id') || undefined,
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listWorkflows(q, { signal }), [params.toString()]);
  const counts = result.status === 'success' ? result.data.state_counts : {};
  const types = result.status === 'success' ? [...result.data.workflow_types] : [];
  if (q.workflow_type && !types.includes(q.workflow_type)) types.push(q.workflow_type);

  const columns: Column<WorkflowSummary>[] = [
    {
      key: 'workflow',
      header: t('workflows.col.workflow'),
      // Reads as "what kind of operation, on what": the id is only the tooltip.
      cell: (w) => (
        <EntityRef
          label={humanize(w.workflow_type)}
          sub={resourceName(w) ?? w.business_key}
          to={`/ops/workflows/${encodeURIComponent(w.id)}`}
          id={w.id}
        />
      ),
    },
    {
      key: 'status',
      header: t('workflows.col.status'),
      cell: (w) => (
        <span className="flex flex-wrap gap-1">
          <EnumBadge group="workflowState" value={w.state} tones={WORKFLOW_TONE} />
          {w.locked_by ? (
            <Badge tone="info" title={t('workflows.leasedBy', { worker: w.locked_by })} icon={false}>
              {t('workflows.leased')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'step', header: t('workflows.col.step'), cell: (w) => <code className="text-xs">{w.current_step ?? '—'}</code> },
    {
      key: 'resource',
      header: t('workflows.col.resource'),
      cell: (w) => {
        const name = resourceName(w);
        if (name) return <DomainLink keyOrName={name} />;
        if (w.resource_id) {
          return (
            <span className="text-sm">
              {w.resource_type} <PlainId id={w.resource_id} />
            </span>
          );
        }
        return '—';
      },
    },
    { key: 'attempt', header: t('workflows.col.attempt'), numeric: true, cell: (w) => w.attempt },
    {
      key: 'failure',
      header: t('workflows.col.failure'),
      cell: (w) =>
        w.failure_code ? (
          <code className="text-xs text-critical" title={w.failure_message ?? undefined}>
            {w.failure_code}
          </code>
        ) : (
          '—'
        ),
    },
    { key: 'created', header: t('workflows.col.created'), cell: (w) => <Since at={w.created_at} /> },
    { key: 'updated', header: t('workflows.col.updated'), cell: (w) => <Since at={w.updated_at} /> },
    { key: 'correlation', header: t('workflows.col.correlation'), cell: (w) => <CorrelationLink id={w.correlation_id} /> },
  ];

  return (
    <>
      <PageHeader title={t('workflows.title')} subtitle={t('workflows.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      <FilterBar>
        <Field label={t('workflows.filter.status')} className="w-full sm:w-64">
          <Select value={q.state ?? ANY} onValueChange={(v) => set('state', v === ANY ? '' : v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('workflows.filter.any')}</SelectItem>
              <SelectItem value="ACTIVE">{t('workflows.filter.active')}</SelectItem>
              {WF_STATES.map((s) => (
                <SelectItem key={s} value={s}>
                  {label('workflowState', s)}
                  {counts[s] !== undefined ? ` (${counts[s]})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t('workflows.filter.type')} className="w-full sm:w-64">
          <Select value={q.workflow_type ?? ANY} onValueChange={(v) => set('workflow_type', v === ANY ? '' : v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('workflows.filter.any')}</SelectItem>
              {types.map((ty) => (
                <SelectItem key={ty} value={ty} title={ty}>
                  {humanize(ty)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {q.resource_id ? (
          <span className="flex h-9 items-center gap-1.5 text-sm">
            {t('workflows.filter.resource')} <PlainId id={q.resource_id} />
            <Button type="button" variant="ghost" size="icon-xs" onClick={() => set('resource_id', '')} aria-label={t('shared.clearFilter')}>
              <X />
            </Button>
          </span>
        ) : null}
        {q.correlation_id ? (
          <span className="flex h-9 items-center gap-1.5 text-sm">
            {t('workflows.filter.correlation')} <PlainId id={q.correlation_id} />
            <Button type="button" variant="ghost" size="icon-xs" onClick={() => set('correlation_id', '')} aria-label={t('shared.clearFilter')}>
              <X />
            </Button>
          </span>
        ) : null}
      </FilterBar>
      {result.status === 'success' && WF_STATES.some((s) => counts[s]) ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {WF_STATES.filter((s) => counts[s]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => set('state', q.state === s ? '' : s)}
              aria-pressed={q.state === s}
              className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2 py-1 text-sm hover:bg-muted aria-pressed:ring-2 aria-pressed:ring-ring"
            >
              <EnumBadge group="workflowState" value={s} tones={WORKFLOW_TONE} />
              <span className="tabular font-semibold">{counts[s]}</span>
            </button>
          ))}
        </div>
      ) : null}
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('workflows.loadFailed')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            {/* No row click: the rows carry links to the domain and the correlation, and a row click would swallow them. */}
            <DataTable columns={columns} rows={result.data.items} rowKey={(w) => w.id} empty={<EmptyState title={t('workflows.empty')} />} />
            {result.data.items.length ? (
              <Pager
                canPrevious={Boolean(q.before)}
                canNext={Boolean(result.data.next_before)}
                onPrevious={() => set('before', '')}
                onNext={() => result.data.next_before && set('before', result.data.next_before)}
                previousLabel={tc('action.newest')}
                nextLabel={tc('action.older')}
              />
            ) : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
